/**
 * Foreground coordinator — serialises the small residual of Excel intents
 * that genuinely require their target workbook to be *active* (foreground),
 * and keeps the user's real focus custody across concurrent multi-workbook
 * runs.
 *
 * ## Why this exists
 *
 * Most Spreadsheet intents can be targeted at a non-active workbook via
 * `RangeAddress.workbookName` / an external-ref `destinationCell`
 * (`[Book.xlsx]Sheet!A1`); the host services them against the named workbook
 * and self-restores focus by the end of the call (see the freeze-panes
 * flicker note in `f2_docs/excel/excel-tools.md`). Those run in the true
 * background and never come through here.
 *
 * A few intents are documented as **active-workbook only** — per
 * `f2_docs/excel/excel-tools.md` these are `HarmonizedRefreshWorksheets`
 * ("Does not support non-active workbooks"), `CopyWorksheet` ("Supports only
 * an active workbook"), and `AddActiveGraph` ("Does not support non-active
 * targets"). Servicing them requires bringing the run's pinned workbook to
 * the foreground. When several runs across different workbooks each try to do
 * that concurrently, Excel's focus ping-pongs between them and the user gets
 * yanked around.
 *
 * `runInForeground` fixes both problems:
 *
 *  1. **Serialise** every foreground window behind one global lock
 *     (`Semaphore(1)`, FIFO), so at most one workbook is ever activated at a
 *     time — no ping-pong.
 *  2. **Custody** the user's real focus: snapshot where the user actually is
 *     *once* at the start of a foreground burst (the 0→1 transition), and
 *     restore it *once* when the burst fully drains (1→0) — not between every
 *     queued window, which would flicker the view back and forth.
 *  3. **Don't fight the user**: if the user manually clicked into a different
 *     workbook while the foreground burst was running, skip the restore and
 *     leave them where they chose to be.
 *
 * ## What this does NOT do
 *
 *  - It does not gate on host readiness (`isBusy` / `isInEditMode`) beyond a
 *    short best-effort probe. The long patient wait + the skip-vs-proceed
 *    ("deferred-host-busy") decision stay with the caller
 *    (`finalizeRefreshAndRecalc` in `ExcelApp.tsx` already owns that). The
 *    coordinator never *drops* a caller's `fn` — a stuck host at most delays
 *    the short probe, then `fn` runs anyway (best-effort).
 *  - It does not decide which intents are foreground-only; callers route the
 *    handful of active-workbook-only operations here at their dispatch sites.
 *
 * ## No-pin / Office.js fast path
 *
 * When `activation.workbookName` is undefined (single-workbook / Office.js
 * mode, where focus is a non-issue), `runInForeground` skips locking,
 * snapshotting, activation, and restore entirely and just runs `fn`. The
 * coordinator is a transparent no-op in that world.
 */

import { Semaphore } from "../shared/utils/semaphore";
import { activateWorksheet, getActiveExcelContext } from "./excelHostOps";
import { waitForHostReady } from "./services/hostReady";

/**
 * What to bring to the foreground for a `runInForeground` call. Both fields
 * are needed to activate: Excel needs a sheet on the target workbook to
 * activate it. Omit `workbookName` (or pass `undefined`) for the no-op fast
 * path.
 */
export interface ForegroundActivation {
  workbookName?: string;
  /** Sheet to activate on the target workbook (the run's session / fallback sheet). */
  sheet?: string;
}

/** The user's real focus, snapshotted at the start of a foreground burst. */
interface FocusSnapshot {
  workbookName?: string;
  sheet: string;
}

export interface RunInForegroundOptions {
  /** Aborts the wait/activate steps; `fn`'s own abort handling still applies. */
  signal?: AbortSignal;
  /** Human-readable label for warn logs (e.g. "trailing refresh"). */
  contextLabel?: string;
  /** Optional console-line logger (`log(...)` in `handleQuery`). */
  log?: (line: string) => void;
}

export interface ForegroundOperationContext {
  /**
   * `false` only when a pinned activation could not be performed. Most
   * foreground operations remain best-effort and may ignore this value; reads
   * whose correctness depends on the pin (such as the date-system probe) must
   * fail closed.
   */
  activationSucceeded: boolean;
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    // Mirror the Office.js / apply-path convention so the outer run-cancel
    // catch routes this to the unified "run cancelled" path.
    throw new DOMException("Aborted", "AbortError");
  }
}

/**
 * Module-level singleton. All Excel runs share one instance so the global
 * foreground lock is genuinely global across concurrent per-workbook runs.
 *
 * Exported for unit tests, which need fresh instances with isolated state
 * (`pending` / `baseline`). Production code should import the shared
 * `foregroundCoordinator` singleton below, never construct its own.
 */
export class ForegroundCoordinator {
  /** Global foreground lock: at most one workbook activated at a time. */
  private readonly lock = new Semaphore(1);
  /** Callers currently waiting for or holding the lock (the "burst" size). */
  private pending = 0;
  /** The user's focus captured once at the start of the current burst. */
  private baseline: FocusSnapshot | null = null;
  /** Guards against re-snapshotting mid-burst if the first probe failed. */
  private baselineTaken = false;
  /** The last workbook/sheet we actually activated (for the restore check). */
  private lastActivated: { workbookName: string; sheet: string } | null = null;
  /** User-selected focus observed between queued foreground activations. */
  private userChosenFocus: FocusSnapshot | null = null;

  /**
   * Run `fn` in a serialized foreground window targeting `activation`, then
   * restore the user's focus.
   *
   * Ordering within the (global, FIFO) lock:
   *   1. Snapshot the user's real focus — only on the 0→1 burst transition.
   *   2. Best-effort short host-idle probe (never drops `fn`).
   *   3. Activate the target workbook/sheet (best-effort).
   *   4. `await fn()`.
   *   5. On the 1→0 burst transition, restore the baseline unless the user
   *      moved to a different workbook meanwhile (don't-fight-user).
   *
   * The lock is always released and the burst counter always decremented in
   * `finally`, even if `fn` throws (e.g. the workbook was closed mid-run).
   */
  async runInForeground<T>(
    activation: ForegroundActivation | undefined,
    fn: (context: ForegroundOperationContext) => Promise<T>,
    options: RunInForegroundOptions = {},
  ): Promise<T> {
    const { signal, contextLabel = "foreground op", log } = options;

    // No-op fast path: nothing to focus (no pin / Office.js). No shared
    // foreground resource to protect, so skip the lock entirely.
    if (!activation?.workbookName) {
      throwIfAborted(signal);
      return fn({ activationSucceeded: true });
    }

    this.pending += 1;
    await this.lock.acquire();
    try {
      throwIfAborted(signal);

      // 0→1 transition: capture where the USER actually is, once per burst.
      if (!this.baselineTaken) {
        this.baselineTaken = true;
        this.baseline = await this.snapshotFocus(contextLabel);
      }

      // Best-effort: give a transient busy/edit state a beat to clear so we
      // don't activate on top of the user mid-type. We never *drop* fn on a
      // stuck host — the caller owns the skip-vs-proceed decision.
      await this.probeIdle(signal);
      throwIfAborted(signal);

      await this.rememberUserFocusBeforeActivation(contextLabel);
      const activationSucceeded = await this.activate(
        activation,
        contextLabel,
      );
      throwIfAborted(signal);

      return await fn({ activationSucceeded });
    } finally {
      this.pending -= 1;
      // 1→0 transition: the foreground burst has fully drained. Restore the
      // user's real focus exactly once.
      if (this.pending === 0) {
        const baseline = this.baseline;
        const lastActivated = this.lastActivated;
        const userChosenFocus = this.userChosenFocus;
        this.baseline = null;
        this.baselineTaken = false;
        this.lastActivated = null;
        this.userChosenFocus = null;
        await this.restore(
          baseline,
          lastActivated,
          userChosenFocus,
          contextLabel,
          log,
        );
      }
      this.lock.release();
    }
  }

  private async snapshotFocus(
    contextLabel: string,
  ): Promise<FocusSnapshot | null> {
    try {
      const ctx = await getActiveExcelContext();
      if (ctx?.activeSheet?.name) {
        return { workbookName: ctx.workbookName, sheet: ctx.activeSheet.name };
      }
    } catch (e) {
      console.warn(
        `[Excel] Foreground baseline snapshot failed before ${contextLabel}; view may shift:`,
        e,
      );
    }
    return null;
  }

  private async probeIdle(signal?: AbortSignal): Promise<void> {
    try {
      // Short default schedule (~3s worst case) — the long patient wait lives
      // with the caller. Result is advisory only; we proceed regardless.
      await waitForHostReady();
    } catch {
      // A probe failure tells us nothing actionable; proceed best-effort.
    }
    throwIfAborted(signal);
  }

  private async activate(
    activation: ForegroundActivation,
    contextLabel: string,
  ): Promise<boolean> {
    const { workbookName, sheet } = activation;
    // Excel external-ref activation needs both a workbook and a sheet; without
    // a sheet there is nothing to activate. Mirrors
    // `reactivatePinnedWorkbookForRefresh`'s `if (!fallback) return`.
    if (!workbookName || !sheet) return false;
    try {
      await activateWorksheet(sheet, { workbookName });
      this.lastActivated = { workbookName, sheet };
      return true;
    } catch (e) {
      // Best-effort, same posture as the old reactivation helpers: a failure
      // (most likely the pinned workbook was closed) is logged and left for
      // `fn`'s own error handling to surface.
      console.warn(
        `[Excel] Foreground activation ("${workbookName}" / "${sheet}") failed before ${contextLabel}; continuing best-effort:`,
        e,
      );
      return false;
    }
  }

  private async rememberUserFocusBeforeActivation(
    contextLabel: string,
  ): Promise<void> {
    if (!this.lastActivated) return;
    try {
      const current = await getActiveExcelContext();
      const currentWb = current?.workbookName;
      const currentSheet = current?.activeSheet?.name;
      if (
        currentWb &&
        currentSheet &&
        currentWb !== this.lastActivated.workbookName
      ) {
        this.userChosenFocus = { workbookName: currentWb, sheet: currentSheet };
      }
    } catch (e) {
      console.warn(
        `[Excel] Foreground user-focus check failed before ${contextLabel}; restore may use the original baseline:`,
        e,
      );
    }
  }

  private async restore(
    baseline: FocusSnapshot | null,
    lastActivated: { workbookName: string; sheet: string } | null,
    userChosenFocus: FocusSnapshot | null,
    contextLabel: string,
    log?: (line: string) => void,
  ): Promise<void> {
    const restoreTarget = userChosenFocus ?? baseline;
    if (!restoreTarget) return;
    try {
      const current = await getActiveExcelContext();
      const currentWb = current?.workbookName;

      // Don't-fight-user: if the user moved to a workbook other than the one
      // we last activated, they took control during the window — leave them.
      if (
        lastActivated &&
        currentWb &&
        currentWb !== lastActivated.workbookName
      ) {
        log?.(
          `↩️ Kept your focus on "${currentWb}" (you switched workbooks while I finished up).`,
        );
        return;
      }

      // Already back where the user was — nothing to restore.
      if (
        currentWb === restoreTarget.workbookName &&
        current?.activeSheet?.name === restoreTarget.sheet
      ) {
        return;
      }

      await activateWorksheet(
        restoreTarget.sheet,
        restoreTarget.workbookName
          ? { workbookName: restoreTarget.workbookName }
          : undefined,
      );
    } catch (e) {
      console.warn(
        `[Excel] Foreground baseline restore failed after ${contextLabel}; leaving focus as-is:`,
        e,
      );
    }
  }
}

/**
 * The shared, process-wide foreground coordinator. Import this (not the
 * class) so every run contends for the same global lock.
 */
export const foregroundCoordinator = new ForegroundCoordinator();
